import React, { useCallback } from "react"

import { useRunTransportInvalidation, useUpdateRunTagsTransport } from "@/runs"
import { toUpdateRunTags } from "@/runs/values"
import { TagEditor, useOrganizationTags, useRecordTagWrite } from "@/tags"

import { MAX_OVERFLOW_DOTS, MAX_VISIBLE_TAGS } from "../constants/tag-layout"

interface RunTagsCellProps {
  runId: string
  initialTags?: string[]
}

export const RunTagsCell = React.memo<RunTagsCellProps>(
  ({ runId, initialTags }) => {
    const { suggestions, invalidate: invalidateOrgTags } = useOrganizationTags()
    const { invalidateRunDetail, invalidateRunsLists } =
      useRunTransportInvalidation()
    const { mutateAsync: updateRunTags } = useUpdateRunTagsTransport()

    const updateTags = useCallback(
      (tags: string[]) =>
        updateRunTags({ run_id: runId, tags: toUpdateRunTags(tags) }),
      [runId, updateRunTags],
    )

    const onWritten = useCallback(() => {
      void invalidateOrgTags()
      void invalidateRunDetail(runId)
      void invalidateRunsLists()
    }, [invalidateOrgTags, invalidateRunDetail, invalidateRunsLists, runId])

    const { tags, writeTags } = useRecordTagWrite({
      errorFallback: "Could not update run tags.",
      errorTitle: "Error updating run tags",
      onWritten,
      recordId: runId,
      resource: "runs",
      rowTags: initialTags,
      updateTags,
    })

    return (
      <TagEditor
        tags={tags}
        onTagsChange={writeTags}
        suggestions={suggestions}
        showInput
        allowRemove
        allowColorEdit
        allowNameEdit
        wrap={false}
        collapseOverflow
        maxVisibleTags={MAX_VISIBLE_TAGS}
        maxOverflowDots={MAX_OVERFLOW_DOTS}
        className="w-full justify-end"
      />
    )
  },
)
RunTagsCell.displayName = "RunTagsCell"
